import React, { useState } from "react";
import { FiExternalLink, FiRefreshCw } from "react-icons/fi";
import Button from "components/ui/Button";
import {
  useGetArtworkConversionsQuery,
  useRetryArtworkConversion,
} from "apis/templateLibrary";

// Template drafts Claude wrote from uploaded client artwork.
//
// Every signboard, brochure or mailcard PDF uploaded to a brand-assets folder
// gets one draft, printed with sample data; files already there do not. Nothing
// here is live: a developer opens the draft, finishes it locally and commits it.
// "Draft again" with a note revises the existing draft; without one it starts
// over.

const STATUS = {
  queued: { label: "Queued", className: "bg-lightgray text-darkgray" },
  converting: { label: "Drafting", className: "bg-blue-100 text-blue-800" },
  ready: { label: "Ready", className: "bg-green-100 text-green-800" },
  failed: { label: "Failed", className: "bg-red-100 text-red-800" },
  stalled: { label: "Stalled", className: "bg-amber-100 text-amber-800" },
};

const statusOf = (conversion) =>
  conversion.stalled
    ? STATUS.stalled
    : STATUS[conversion.status] || STATUS.queued;

// What the row says under the file name while a draft waits or runs.
const progressOf = (conversion) => {
  if (conversion.stalled) return null;
  if (conversion.status === "queued") {
    return conversion.note
      ? `Waiting to revise: ${conversion.note}`
      : conversion.progress || "Waiting for the draft worker";
  }
  return conversion.status === "converting" ? conversion.progress : null;
};

const sizeOf = ({ width, height }) => `${width} × ${height}mm`;

// The finished draft printed with the artwork's own text and measured against
// the artwork: size per page, how many pixels match, and a map of where not.
const MatchSummary = ({ match }) => {
  if (!match) return null;
  if (match.error) {
    return (
      <div className="mt-1 text-[11px] text-amber-700">
        Not matched against the artwork: {match.error}
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-col gap-0.5 text-[11px]">
      {match.samePageCount ? null : (
        <div className="text-red-600">
          The draft prints a different number of pages from the artwork.
        </div>
      )}
      {match.pages.map((page) => (
        <div key={page.page} className="flex flex-wrap items-center gap-x-2">
          <span className="text-darkgray">Page {page.page}:</span>
          {page.sameSize ? (
            <span className="text-darkgray">
              same size ({sizeOf(page.artworkMm)})
            </span>
          ) : (
            <span className="text-red-600">
              size differs — artwork {sizeOf(page.artworkMm)}, draft{" "}
              {page.printMm ? sizeOf(page.printMm) : "no page"}
            </span>
          )}
          <span className="font-medium text-black">
            {page.matchingPixels}% match
          </span>
          {page.mapUrl ? (
            <a
              href={page.mapUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:underline"
            >
              Where it differs
            </a>
          ) : null}
        </div>
      ))}
    </div>
  );
};

const ConversionRow = ({ conversion, onRetry, retrying }) => {
  const [note, setNote] = useState("");
  const status = statusOf(conversion);
  const progress = progressOf(conversion);
  const canRetry =
    conversion.stalled || ["ready", "failed"].includes(conversion.status);

  return (
    <div className="border-t border-mediumgray px-3.5 py-3 first:border-t-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={conversion.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="truncate text-[13px] font-medium text-black hover:underline"
            >
              {conversion.fileName}
            </a>
            <span className="rounded bg-lightgray px-1.5 py-0.5 text-[10px] text-darkgray">
              {conversion.type}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${status.className}`}
            >
              {status.label}
            </span>
          </div>
          {progress ? (
            <div className="mt-1 text-[11px] text-darkgray">{progress}</div>
          ) : null}
          {conversion.error ? (
            <div className="mt-1 text-[11px] text-red-600">
              {conversion.error}
            </div>
          ) : null}
          {conversion.previewError ? (
            <div className="mt-1 text-[11px] text-red-600">
              The draft did not run with sample data: {conversion.previewError}
            </div>
          ) : null}
          {conversion.status === "ready" ? (
            <MatchSummary match={conversion.match} />
          ) : null}
          {conversion.status === "ready" && conversion.fonts?.length ? (
            <div className="mt-1 text-[11px] text-darkgray">
              Fonts needed from the agency: {conversion.fonts.join(", ")}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2 sm:flex-shrink-0">
          {conversion.previewUrl ? (
            <a
              href={conversion.previewUrl}
              target="_blank"
              rel="noreferrer"
              className="outline-button flex items-center gap-1.5 !py-1.5 !px-2.5 !text-[12px]"
            >
              <FiExternalLink className="h-3.5 w-3.5" />
              Open preview PDF
            </a>
          ) : null}
          {conversion.templateUrl ? (
            <a
              href={conversion.templateUrl}
              target="_blank"
              rel="noreferrer"
              className="outline-button flex items-center gap-1.5 !py-1.5 !px-2.5 !text-[12px]"
            >
              <FiExternalLink className="h-3.5 w-3.5" />
              Open template.js
            </a>
          ) : null}
        </div>
      </div>

      {canRetry ? (
        <div className="mt-2.5 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="What should change? e.g. move the headline down 5mm — leave empty to start over"
            className="min-w-0 flex-1 rounded border border-mediumgray px-2.5 py-1.5 text-[12px]"
          />
          <Button
            className="outline-button flex items-center gap-1.5 !py-1.5 !px-2.5 !text-[12px] sm:flex-shrink-0"
            loading={retrying}
            onClick={() => onRetry({ id: conversion.id, note: note.trim() })}
          >
            <FiRefreshCw className="h-3.5 w-3.5" />
            {note.trim() && conversion.templateUrl ? "Revise" : "Draft again"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const ArtworkConversionsBrowser = ({ networkId }) => {
  const { data, isPending, isError, error } =
    useGetArtworkConversionsQuery(networkId);
  const {
    mutate: retry,
    variables,
    isPending: isRetrying,
  } = useRetryArtworkConversion();

  if (isPending) {
    return (
      <p className="py-6 text-[13px] text-darkgray">
        <i className="fa-solid fa-spinner animate-spin mr-2"></i>
        Loading drafts …
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-[13px] text-red-600">
        Error loading drafts: {error?.message}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[12px] text-darkgray">
        Signboard, brochure and mailcard PDFs uploaded to brand assets get a
        template draft written automatically, printed with sample data so you
        can check it here. Open template.js, finish it locally, then commit.
      </p>

      {data.length === 0 ? (
        <p className="text-[13px] italic text-darkgray">
          No drafts for this network yet
        </p>
      ) : (
        <div className="rounded-lg border border-mediumgray bg-white">
          {data.map((conversion) => (
            <ConversionRow
              key={conversion.id}
              conversion={conversion}
              onRetry={retry}
              retrying={isRetrying && variables?.id === conversion.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtworkConversionsBrowser;
